import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { persistClientDataImmediate } from '../../config/firebaseSync';
import {
  calculatePeProfitMetrics,
  resolveCalcForPlanungsBasis,
} from '../../hooks/usePeProfitCalculations';
import { calculateAllMetrics } from '../../store/selectors/calculations';
import useAppStore from '../../store/index';
import {
  PE_PROFIT_NEUER_MITARBEITER_ID,
  type Dienstleistungserweiterung,
  type PEProfitCalculations,
  type PersonalAnpassung,
  type PlanungsBasisDatenTyp,
} from '../../types/peProfit';
import { buildBankKennzahlenPayload } from './bankKennzahlen';

type GenerateBankerPitchResponse = {
  text?: string;
};

type PersonalDeltaFact = {
  mitarbeiterId: string;
  name: string;
  isProduktiv: boolean;
  deltaMonat: number;
  deltaJahr: number;
  entlassen: boolean;
};

const employeeIdSet = (list: unknown): Set<string> => {
  const ids = new Set<string>();
  if (!Array.isArray(list)) return ids;
  for (const entry of list) {
    const id = entry && typeof entry === 'object' ? String((entry as { id?: unknown }).id ?? '') : '';
    if (id) ids.add(id);
  }
  return ids;
};

const AZUBI_TAETIGKEITEN = new Set(['Azubi', 'Prod. Azubi', 'produktiver Azubi', 'Assistenz']);

/**
 * Harte Klassifikation wie im Cockpit:
 * personalData → produktiv (außer Azubi-Tätigkeit fälschlich dort)
 * unproductivePersonalData + azubiAssistenzData → unproduktiv
 */
export const classifyPersonalIsProduktiv = (
  mitarbeiterId: string,
  clientData: Record<string, unknown> | null | undefined
): boolean => {
  const id = String(mitarbeiterId ?? '');
  if (!id) return false;
  if (id === PE_PROFIT_NEUER_MITARBEITER_ID) return true;

  const produktivIds = employeeIdSet(clientData?.personalData);
  const unproduktivIds = employeeIdSet(clientData?.unproductivePersonalData);
  const azubiIds = employeeIdSet(clientData?.azubiAssistenzData);

  // Azubi/Unproduktiv zuerst — nie als produktiv werten
  if (azubiIds.has(id) || unproduktivIds.has(id)) return false;

  if (produktivIds.has(id)) {
    const ma = Array.isArray(clientData?.personalData)
      ? (clientData.personalData as Array<{ id?: string; taetigkeit?: string }>).find(
          (entry) => String(entry?.id ?? '') === id
        )
      : undefined;
    if (ma && AZUBI_TAETIGKEITEN.has(String(ma.taetigkeit ?? ''))) return false;
    return true;
  }

  // Unbekannt → unproduktiv (kein Ratespiel Richtung produktiv)
  return false;
};

export const buildPersonalProduktivDeltas = (
  personalAnpassungen: PersonalAnpassung[] | undefined,
  clientData: Record<string, unknown> | null | undefined,
  zeitraum: number
): {
  deltaProduktiv: number;
  deltaUnproduktiv: number;
  deltaProduktivJahr: number;
  deltaUnproduktivJahr: number;
  personalDeltas: PersonalDeltaFact[];
} => {
  const scale = Number(zeitraum) > 0 ? Number(zeitraum) : 12;
  let deltaProduktiv = 0;
  let deltaUnproduktiv = 0;
  const personalDeltas: PersonalDeltaFact[] = [];

  for (const pa of personalAnpassungen ?? []) {
    if (pa.aktiv === false) continue;
    const ist = Number(pa.istWert) || 0;
    const ziel = pa.entlassen ? 0 : Number(pa.neuerZielwert) || 0;
    const deltaMonat = ziel - ist;
    if (!deltaMonat) continue;

    const isProduktiv = classifyPersonalIsProduktiv(pa.mitarbeiterId, clientData);
    if (isProduktiv) deltaProduktiv += deltaMonat;
    else deltaUnproduktiv += deltaMonat;

    personalDeltas.push({
      mitarbeiterId: String(pa.mitarbeiterId ?? ''),
      name: String(pa.mitarbeiterName ?? '').trim() || 'Unbekannt',
      isProduktiv,
      deltaMonat,
      deltaJahr: deltaMonat * scale,
      entlassen: Boolean(pa.entlassen),
    });
  }

  return {
    deltaProduktiv,
    deltaUnproduktiv,
    deltaProduktivJahr: deltaProduktiv * scale,
    deltaUnproduktivJahr: deltaUnproduktiv * scale,
    personalDeltas,
  };
};

const buildNeueDienstleistungenPayload = (
  liste: Dienstleistungserweiterung[] | undefined
): Array<{ id: string; name: string; jahresumsatzNetto: number; startMonat: number; aktiv: boolean }> =>
  (liste ?? [])
    .filter((item) => item && item.aktiv !== false)
    .map((item) => ({
      id: String(item.id ?? ''),
      name: String(item.name ?? '').trim(),
      jahresumsatzNetto: Number(item.jahresumsatzNetto) || 0,
      startMonat: Number(item.startMonat) || 1,
      aktiv: true,
    }))
    .filter((item) => item.name.length > 0);

/**
 * Ruft generateBankerPitch auf und speichert response.data.text
 * persistent am aktiven Planungs-Szenario.
 */
/** Banker-Pitch braucht Jahres-PnL — bei Monatsansicht automatisch umschalten und neu rechnen. */
const resolvePeProfitForBankerPitch = (
  peProfit: PEProfitCalculations
): PEProfitCalculations => {
  const state = useAppStore.getState();

  if (state.peProfitPrognose?.ansichtsModus !== 'jahr') {
    state.setAnsichtsModus('jahr');
  }

  const after = useAppStore.getState();
  const prognose = {
    ...after.peProfitPrognose,
    ansichtsModus: 'jahr' as const,
  };

  const activeId = after.activeSzenarioId;
  const archived = (after.planungsArchiv ?? []).find((entry) => entry.id === activeId);
  const basisDatenTyp: PlanungsBasisDatenTyp =
    archived?.basisDatenTyp ?? after.peProfitBasisDatenTyp ?? 'start';

  const calc = calculateAllMetrics(after.clientData, after.settings);
  const calcForBasis = resolveCalcForPlanungsBasis(
    calc,
    basisDatenTyp,
    after.clientData,
    after.settings
  );

  const computed = calculatePeProfitMetrics(
    calcForBasis,
    prognose,
    after.peProfitDataVersion ?? 0
  );

  // Fallback: bereits gelieferte Jahres-Berechnung nutzen, falls Store-Calc noch nicht ready
  if (!computed.ready && peProfit?.ready && peProfit.ansichtsModus === 'jahr') {
    return peProfit;
  }

  return computed;
};

export async function generateAndPersistBankerPitch(
  peProfit: PEProfitCalculations
): Promise<void> {
  const state = useAppStore.getState();
  const activeSzenarioId = state.activeSzenarioId;

  if (!activeSzenarioId) {
    throw new Error(
      'Kein aktives Szenario — bitte zuerst ein Szenario öffnen oder neu anlegen.'
    );
  }

  const peProfitJahr = resolvePeProfitForBankerPitch(peProfit);

  if (!peProfitJahr?.ready) {
    throw new Error('PE-Profit-Berechnung ist noch nicht bereit.');
  }

  const ansichtsModus = 'jahr';
  const stateAfter = useAppStore.getState();

  const personalFacts = buildPersonalProduktivDeltas(
    stateAfter.peProfitPrognose?.personalAnpassungen,
    stateAfter.clientData,
    peProfitJahr.zeitraum
  );

  const neueDienstleistungen = buildNeueDienstleistungenPayload(
    stateAfter.peProfitPrognose?.neueDienstleistungen
  );

  const scale = Number(peProfitJahr.zeitraum) > 0 ? Number(peProfitJahr.zeitraum) : 12;

  const gemeinkostenDetailAnpassungen = (
    stateAfter.peProfitPrognose?.gemeinkostenDetailAnpassungen ?? []
  ).map((gk) => {
    const istMonat = Number(gk.istWert) || 0;
    const zielMonat = Number(gk.neuerZielwert) || 0;
    return {
      id: String(gk.id ?? ''),
      bwaKey: String(gk.bwaKey ?? ''),
      label: String(gk.label ?? ''),
      ...(typeof gk.notiz === 'string' && gk.notiz.length > 0 ? { notiz: gk.notiz } : {}),
      istWert: istMonat * scale,
      neuerZielwert: zielMonat * scale,
      delta: (zielMonat - istMonat) * scale,
    };
  });

  const bankKennzahlen = buildBankKennzahlenPayload(
    peProfitJahr.ist,
    peProfitJahr.prognose
  );

  const payload = {
    ist: peProfitJahr.ist,
    prognose: peProfitJahr.prognose,
    abweichung: peProfitJahr.abweichung,
    meta: peProfitJahr.meta,
    ansichtsModus,
    zeitraum: peProfitJahr.zeitraum,
    szenarioName: peProfitJahr.szenarioName,
    neueInvestitionen: stateAfter.peProfitPrognose?.neueInvestitionen ?? [],
    // Fix 1: Namen/Umsatztreiber explizit für die CF
    neueDienstleistungen,
    neueDienstleistungNamen: neueDienstleistungen.map((item) => item.name),
    // Detailkosten: Jahreswerte (× zeitraum) + label/notiz unabgeschnitten
    gemeinkostenDetailAnpassungen,
    // Begründung Materialkosten (Wareneinsatz)
    materialNotiz: String(stateAfter.peProfitPrognose?.materialNotiz ?? ''),
    // Fix 2: harte Produktiv/Unproduktiv-Fakten (Gesellin vs Azubi)
    deltaProduktiv: personalFacts.deltaProduktiv,
    deltaUnproduktiv: personalFacts.deltaUnproduktiv,
    deltaProduktivJahr: personalFacts.deltaProduktivJahr,
    deltaUnproduktivJahr: personalFacts.deltaUnproduktivJahr,
    personalDeltas: personalFacts.personalDeltas,
    // Bank-/BWA-Näherung: BE vor/nach Steuern + Cashflow (Ist & Prognose, Jahreswerte)
    bankKennzahlen,
  };

  const generatePitch = httpsCallable(functions, 'generateBankerPitch');
  const response = await generatePitch(payload);
  const text = (response.data as GenerateBankerPitchResponse | null)?.text;

  if (!text || !String(text).trim()) {
    throw new Error('Die KI hat keinen gültigen Banker-Pitch-Text geliefert.');
  }

  useAppStore.getState().setActiveSzenarioBankerPitch(String(text).trim());

  const synced = await persistClientDataImmediate();
  if (!synced) {
    throw new Error(
      'Banker-Pitch wurde lokal gespeichert, konnte aber nicht in die Cloud synchronisiert werden. Bitte kurz warten und erneut versuchen.'
    );
  }
}
